/**
 * Project template setup for Settings: the projects a template is already
 * set up in, and Install, which asks Denker to set it up in a new chat, as
 * `/new <request>` does. Denker asks there whether to use a new project or
 * the one in view, and runs the project-setup skill; Settings never shows a
 * setup form.
 */
import { useCallback, useMemo } from "react";
import {
  listPluginProjects,
  type PluginProjectInstance,
} from "@/api/plugin-projects";
import { dispatchUserMessage } from "@/hooks/use-dispatch-user-message";
import {
  useRetryableLoad,
  type RetryableLoad,
} from "@/hooks/use-retryable-load";
import { requestStandaloneNewChat } from "@/lib/new-chat-command";
import { useProjectStore } from "@/stores/project-store";

const NO_INSTANCES: PluginProjectInstance[] = [];

/** The user's instances of one template; none are loaded while `enabled` is false. */
export function usePluginProjects(
  slug: string,
  enabled: boolean,
): RetryableLoad<PluginProjectInstance[]> {
  const load = useCallback(
    () => (enabled ? listPluginProjects(slug) : Promise.resolve(NO_INSTANCES)),
    [slug, enabled],
  );
  return useRetryableLoad(load, NO_INSTANCES);
}

/** The active projects the instances are set up in, by name, once each. */
export function useSetUpProjectNames(
  instances: readonly PluginProjectInstance[],
): string[] {
  const projects = useProjectStore((state) => state.projects);
  return useMemo(() => {
    const ids = new Set(instances.map((instance) => instance.project_id));
    return projects
      .filter((project) => project.status === "active" && ids.has(project.id))
      .map((project) => project.name);
  }, [instances, projects]);
}

/** Sends `request` to Denker as the first message of a new chat; resolves
 *  true once Denker accepted it, false when it was not sent or refused. */
export async function askDenkerInNewChat(request: string): Promise<boolean> {
  requestStandaloneNewChat();
  const result = dispatchUserMessage({
    text: request,
    literalText: true,
    composerId: "plugin-install",
  });
  return result.sent && (await result.accepted);
}
